/**
 * Order Management Service
 * Frontend API client for order management operations. Every method resolves to the
 * response BODY.
 *
 * TypeScript since ST-00227 (was orderService.js); the default export (one instance) and
 * every method name / signature are unchanged for the JS pages that still import it.
 *
 * Path segments are encoded: weighbridge order numbers contain "/" (e.g.
 * `Mafube(Exxaro)-to-Destone/Stamlers`), and unencoded they split the path so the API
 * answered 404 for every call about such an order. The server (Express) decodes the
 * `:orderNo` parameter back. Ids that are numbers or uuids are unchanged by the encoding.
 *
 * The legacy location sync (`getLocationSyncStatus`, `triggerLocationSync`) is gone with
 * its server router (/api/location-sync).
 */

import apiService from './apiService'; // Reuse existing axios instance
import type {
  ApiWriteResponse,
  AvailableOrdersResponse,
  AvailableVehiclesResponse,
  BulkUploadResponse,
  BulkUploadRow,
  CompaniesResponse,
  CompanyId,
  CompanyLocationAssociationType,
  CompanyLocationsResponse,
  CompanyPayload,
  CompanyResponse,
  CreateOrderPayload,
  CreateOrderResponse,
  DriverPayload,
  DriverResponse,
  DriversResponse,
  FleetStatsResponse,
  LineItemPayload,
  LineItemResponse,
  LineItemsResponse,
  LocationListResponse,
  LocationPayload,
  LocationResponse,
  MasterDataCleanupResponse,
  MasterDataOrderService,
  MasterDataSyncResponse,
  MasterDataSyncStatus,
  OrderAmendmentData,
  OrderAmendmentsResponse,
  OrderDashboardResponse,
  OrderDataCheckInput,
  OrderEventsResponse,
  OrderHistoryResponse,
  OrderListQuery,
  OrderPagesService,
  OrderResponse,
  OrdersListResponse,
  ProductCategoriesResponse,
  ProductListResponse,
  ProductPayload,
  ProductResponse,
  TruckAllocationsResponse,
  UpdateOrderPayload,
  VehiclePayload,
  VehicleResponse,
  VehiclesResponse,
} from '../types/clientPages/orderManagement';

/** Query parameters a list endpoint accepts (axios `params`). */
type ListFilters = Record<string, string | number | boolean | undefined>;

/** One URL path segment: an order number, a uuid or an integer id. */
const seg = (value: string | number): string => encodeURIComponent(String(value));

class OrderService implements MasterDataOrderService, OrderPagesService {
  // ===========================
  // PRODUCTS
  // ===========================

  async getProducts(filters: ListFilters = {}): Promise<ProductListResponse> {
    const response = await apiService.get<ProductListResponse>('/products', { params: filters });
    return response.data;
  }

  async getProduct(productId: number | string): Promise<ProductResponse> {
    const response = await apiService.get<ProductResponse>(`/products/${seg(productId)}`);
    return response.data;
  }

  async getProductCategories(): Promise<ProductCategoriesResponse> {
    const response = await apiService.get<ProductCategoriesResponse>('/products/categories');
    return response.data;
  }

  async createProduct(productData: ProductPayload): Promise<ProductResponse & ApiWriteResponse> {
    const response = await apiService.post<ProductResponse & ApiWriteResponse>(
      '/products',
      productData
    );
    return response.data;
  }

  async updateProduct(
    productId: number | string,
    updates: Partial<ProductPayload>
  ): Promise<ProductResponse & ApiWriteResponse> {
    const response = await apiService.put<ProductResponse & ApiWriteResponse>(
      `/products/${seg(productId)}`,
      updates
    );
    return response.data;
  }

  async deleteProduct(productId: number | string): Promise<ApiWriteResponse> {
    const response = await apiService.delete<ApiWriteResponse>(`/products/${seg(productId)}`);
    return response.data;
  }

  // ===========================
  // LOCATIONS
  // ===========================

  async getLocations(filters: ListFilters = {}): Promise<LocationListResponse> {
    const response = await apiService.get<LocationListResponse>('/locations', { params: filters });
    return response.data;
  }

  async getLocation(locationId: number | string): Promise<LocationResponse> {
    const response = await apiService.get<LocationResponse>(`/locations/${seg(locationId)}`);
    return response.data;
  }

  async getDispatchLocations(): Promise<LocationListResponse> {
    const response = await apiService.get<LocationListResponse>('/locations/dispatch');
    return response.data;
  }

  async getReceiptLocations(): Promise<LocationListResponse> {
    const response = await apiService.get<LocationListResponse>('/locations/receipt');
    return response.data;
  }

  /**
   * Get distinct dispatch locations from LexiBridgeSource
   * Returns array of location name strings
   */
  async getSourceDispatchLocations(): Promise<string[]> {
    const response = await apiService.get<string[]>('/data/locations/dispatch');
    return response.data;
  }

  /**
   * Get distinct receipt locations from LexiBridgeSource
   * Optionally filtered by a selected dispatch location
   * Returns array of location name strings
   */
  async getSourceReceiptLocations(dispatchLocation: string | null = null): Promise<string[]> {
    const params = dispatchLocation ? { dispatchLocation } : {};
    const response = await apiService.get<string[]>('/data/locations/receipt', { params });
    return response.data;
  }

  async createLocation(
    locationData: LocationPayload
  ): Promise<LocationResponse & ApiWriteResponse> {
    const response = await apiService.post<LocationResponse & ApiWriteResponse>(
      '/locations',
      locationData
    );
    return response.data;
  }

  async updateLocation(
    locationId: number | string,
    updates: Partial<LocationPayload>
  ): Promise<LocationResponse & ApiWriteResponse> {
    const response = await apiService.put<LocationResponse & ApiWriteResponse>(
      `/locations/${seg(locationId)}`,
      updates
    );
    return response.data;
  }

  async deleteLocation(locationId: number | string): Promise<ApiWriteResponse> {
    const response = await apiService.delete<ApiWriteResponse>(`/locations/${seg(locationId)}`);
    return response.data;
  }

  // ===========================
  // COMPANIES
  // ===========================

  async getCompanies(filters: ListFilters = {}): Promise<CompaniesResponse> {
    const response = await apiService.get<CompaniesResponse>('/companies', { params: filters });
    return response.data;
  }

  async getCompany(companyId: CompanyId): Promise<CompanyResponse> {
    const response = await apiService.get<CompanyResponse>(`/companies/${seg(companyId)}`);
    return response.data;
  }

  async getCompanyLocations(
    companyId: CompanyId,
    type: CompanyLocationAssociationType | null = null
  ): Promise<CompanyLocationsResponse> {
    const params = type ? { type } : {};
    const response = await apiService.get<CompanyLocationsResponse>(
      `/companies/${seg(companyId)}/locations`,
      { params }
    );
    return response.data;
  }

  async createCompany(companyData: CompanyPayload): Promise<CompanyResponse & ApiWriteResponse> {
    const response = await apiService.post<CompanyResponse & ApiWriteResponse>(
      '/companies',
      companyData
    );
    return response.data;
  }

  async updateCompany(
    companyId: CompanyId,
    updates: CompanyPayload
  ): Promise<CompanyResponse & ApiWriteResponse> {
    const response = await apiService.put<CompanyResponse & ApiWriteResponse>(
      `/companies/${seg(companyId)}`,
      updates
    );
    return response.data;
  }

  async deleteCompany(companyId: CompanyId): Promise<ApiWriteResponse> {
    const response = await apiService.delete<ApiWriteResponse>(`/companies/${seg(companyId)}`);
    return response.data;
  }

  async associateCompanyLocation(
    companyId: CompanyId,
    locationId: number | string,
    associationType: CompanyLocationAssociationType
  ): Promise<ApiWriteResponse> {
    const response = await apiService.post<ApiWriteResponse>(
      `/companies/${seg(companyId)}/locations`,
      {
        location_id: locationId,
        association_type: associationType,
      }
    );
    return response.data;
  }

  async removeCompanyLocationAssociation(
    companyId: CompanyId,
    locationId: number | string
  ): Promise<ApiWriteResponse> {
    const response = await apiService.delete<ApiWriteResponse>(
      `/companies/${seg(companyId)}/locations/${seg(locationId)}`
    );
    return response.data;
  }

  // ===========================
  // MASTER DATA SYNC
  // ===========================

  async getMasterDataSyncStatus(): Promise<MasterDataSyncStatus> {
    const response = await apiService.get<MasterDataSyncStatus>('/master-data-sync/status');
    return response.data;
  }

  async triggerMasterDataSync(entity: string | null = null): Promise<MasterDataSyncResponse> {
    const url = entity ? `/master-data-sync/trigger/${seg(entity)}` : '/master-data-sync/trigger';
    const response = await apiService.post<MasterDataSyncResponse>(url);
    return response.data;
  }

  async triggerMasterDataCleanup(): Promise<MasterDataCleanupResponse> {
    const response = await apiService.post<MasterDataCleanupResponse>('/master-data-sync/cleanup');
    return response.data;
  }

  // ===========================
  // ORDERS
  // ===========================

  async getOrders(filters: OrderListQuery = {}): Promise<OrdersListResponse> {
    const response = await apiService.get<OrdersListResponse>('/orders', { params: filters });
    return response.data;
  }

  async getOrder(orderNo: string): Promise<OrderResponse> {
    const response = await apiService.get<OrderResponse>(`/orders/${seg(orderNo)}`);
    return response.data;
  }

  async getDashboardData(): Promise<OrderDashboardResponse> {
    const response = await apiService.get<OrderDashboardResponse>('/orders/dashboard');
    return response.data;
  }

  async createOrder(orderData: CreateOrderPayload): Promise<CreateOrderResponse> {
    const response = await apiService.post<CreateOrderResponse>('/orders', orderData);
    return response.data;
  }

  async updateOrder(
    orderNo: string,
    updates: UpdateOrderPayload
  ): Promise<OrderResponse & ApiWriteResponse> {
    const response = await apiService.put<OrderResponse & ApiWriteResponse>(
      `/orders/${seg(orderNo)}`,
      updates
    );
    return response.data;
  }

  async updateOrderStatus(
    orderNo: string,
    status: string,
    reason: string | null = null
  ): Promise<OrderResponse & ApiWriteResponse> {
    const response = await apiService.post<OrderResponse & ApiWriteResponse>(
      `/orders/${seg(orderNo)}/status`,
      { status, reason }
    );
    return response.data;
  }

  async cancelOrder(orderNo: string, reason: string): Promise<OrderResponse & ApiWriteResponse> {
    const response = await apiService.delete<OrderResponse & ApiWriteResponse>(
      `/orders/${seg(orderNo)}`,
      { data: { reason } }
    );
    return response.data;
  }

  async getOrderHistory(orderNo: string): Promise<OrderHistoryResponse> {
    const response = await apiService.get<OrderHistoryResponse>(`/orders/${seg(orderNo)}/history`);
    return response.data;
  }

  async getOrderEvents(orderNo: string): Promise<OrderEventsResponse> {
    const response = await apiService.get<OrderEventsResponse>(`/orders/${seg(orderNo)}/events`);
    return response.data;
  }

  // ===========================
  // AMENDMENTS
  // ===========================

  async createAmendment(
    orderNo: string,
    amendmentData: OrderAmendmentData,
    reason: string
  ): Promise<ApiWriteResponse> {
    const response = await apiService.post<ApiWriteResponse>(`/orders/${seg(orderNo)}/amendments`, {
      amendment_data: amendmentData,
      reason,
    });
    return response.data;
  }

  async getOrderAmendments(orderNo: string): Promise<OrderAmendmentsResponse> {
    const response = await apiService.get<OrderAmendmentsResponse>(
      `/orders/${seg(orderNo)}/amendments`
    );
    return response.data;
  }

  // ===========================
  // LINE ITEMS
  // ===========================

  async getLineItems(orderNo: string): Promise<LineItemsResponse> {
    const response = await apiService.get<LineItemsResponse>(`/orders/${seg(orderNo)}/line-items`);
    return response.data;
  }

  async getLineItem(orderNo: string, lineItemId: number | string): Promise<LineItemResponse> {
    const response = await apiService.get<LineItemResponse>(
      `/orders/${seg(orderNo)}/line-items/${seg(lineItemId)}`
    );
    return response.data;
  }

  async addLineItem(
    orderNo: string,
    lineItemData: LineItemPayload
  ): Promise<LineItemResponse & ApiWriteResponse> {
    const response = await apiService.post<LineItemResponse & ApiWriteResponse>(
      `/orders/${seg(orderNo)}/line-items`,
      lineItemData
    );
    return response.data;
  }

  async updateLineItem(
    orderNo: string,
    lineItemId: number | string,
    updates: LineItemPayload
  ): Promise<LineItemResponse & ApiWriteResponse> {
    const response = await apiService.put<LineItemResponse & ApiWriteResponse>(
      `/orders/${seg(orderNo)}/line-items/${seg(lineItemId)}`,
      updates
    );
    return response.data;
  }

  async deleteLineItem(orderNo: string, lineItemId: number | string): Promise<ApiWriteResponse> {
    const response = await apiService.delete<ApiWriteResponse>(
      `/orders/${seg(orderNo)}/line-items/${seg(lineItemId)}`
    );
    return response.data;
  }

  // ===========================
  // VEHICLES
  // ===========================

  async getVehicles(filters: ListFilters = {}): Promise<VehiclesResponse> {
    const response = await apiService.get<VehiclesResponse>('/vehicles', { params: filters });
    return response.data;
  }

  async getVehicle(vehicleId: string): Promise<VehicleResponse> {
    const response = await apiService.get<VehicleResponse>(`/vehicles/${seg(vehicleId)}`);
    return response.data;
  }

  async getVehicleStats(): Promise<FleetStatsResponse> {
    const response = await apiService.get<FleetStatsResponse>('/vehicles/stats');
    return response.data;
  }

  async createVehicle(vehicleData: VehiclePayload): Promise<VehicleResponse & ApiWriteResponse> {
    const response = await apiService.post<VehicleResponse & ApiWriteResponse>(
      '/vehicles',
      vehicleData
    );
    return response.data;
  }

  async updateVehicle(
    vehicleId: string,
    updates: Partial<VehiclePayload>
  ): Promise<VehicleResponse & ApiWriteResponse> {
    const response = await apiService.put<VehicleResponse & ApiWriteResponse>(
      `/vehicles/${seg(vehicleId)}`,
      updates
    );
    return response.data;
  }

  async deleteVehicle(vehicleId: string): Promise<ApiWriteResponse> {
    const response = await apiService.delete<ApiWriteResponse>(`/vehicles/${seg(vehicleId)}`);
    return response.data;
  }

  async bulkWhitelistVehicles(
    vehicleIds: string[],
    whitelisted: boolean
  ): Promise<ApiWriteResponse & { updated?: number }> {
    const response = await apiService.post<ApiWriteResponse & { updated?: number }>(
      '/vehicles/bulk-whitelist',
      {
        vehicle_ids: vehicleIds,
        whitelisted,
      }
    );
    return response.data;
  }

  async addDriverToVehicle(
    vehicleId: string,
    driverId: string,
    isPrimary = false
  ): Promise<ApiWriteResponse> {
    const response = await apiService.post<ApiWriteResponse>(
      `/vehicles/${seg(vehicleId)}/drivers`,
      {
        driver_id: driverId,
        is_primary: isPrimary,
      }
    );
    return response.data;
  }

  async removeDriverFromVehicle(vehicleId: string, driverId: string): Promise<ApiWriteResponse> {
    const response = await apiService.delete<ApiWriteResponse>(
      `/vehicles/${seg(vehicleId)}/drivers/${seg(driverId)}`
    );
    return response.data;
  }

  // ===========================
  // DRIVERS
  // ===========================

  async getDrivers(filters: ListFilters = {}): Promise<DriversResponse> {
    const response = await apiService.get<DriversResponse>('/drivers', { params: filters });
    return response.data;
  }

  async getDriver(driverId: string): Promise<DriverResponse> {
    const response = await apiService.get<DriverResponse>(`/drivers/${seg(driverId)}`);
    return response.data;
  }

  async getDriverStats(): Promise<FleetStatsResponse> {
    const response = await apiService.get<FleetStatsResponse>('/drivers/stats');
    return response.data;
  }

  async createDriver(driverData: DriverPayload): Promise<DriverResponse & ApiWriteResponse> {
    const response = await apiService.post<DriverResponse & ApiWriteResponse>(
      '/drivers',
      driverData
    );
    return response.data;
  }

  async updateDriver(
    driverId: string,
    updates: Partial<DriverPayload>
  ): Promise<DriverResponse & ApiWriteResponse> {
    const response = await apiService.put<DriverResponse & ApiWriteResponse>(
      `/drivers/${seg(driverId)}`,
      updates
    );
    return response.data;
  }

  async deleteDriver(driverId: string): Promise<ApiWriteResponse> {
    const response = await apiService.delete<ApiWriteResponse>(`/drivers/${seg(driverId)}`);
    return response.data;
  }

  // ===========================
  // BULK UPLOAD
  // ===========================

  async bulkUploadVehiclesDrivers(rows: BulkUploadRow[]): Promise<BulkUploadResponse> {
    const response = await apiService.post<BulkUploadResponse>('/bulk-upload/vehicles-drivers', {
      rows,
    });
    return response.data;
  }

  async bulkUploadOrderAllocations(rows: BulkUploadRow[]): Promise<BulkUploadResponse> {
    const response = await apiService.post<BulkUploadResponse>('/bulk-upload/order-allocations', {
      rows,
    });
    return response.data;
  }

  // ===========================
  // ORDER ALLOCATIONS
  // ===========================

  async getOrderAllocations(filters: ListFilters = {}): Promise<TruckAllocationsResponse> {
    const response = await apiService.get<TruckAllocationsResponse>('/order-allocations', {
      params: filters,
    });
    return response.data;
  }

  async getAvailableOrders(filters: ListFilters = {}): Promise<AvailableOrdersResponse> {
    const response = await apiService.get<AvailableOrdersResponse>(
      '/order-allocations/available-orders',
      { params: filters }
    );
    return response.data;
  }

  async getAvailableVehicles(filters: ListFilters = {}): Promise<AvailableVehiclesResponse> {
    const response = await apiService.get<AvailableVehiclesResponse>(
      '/order-allocations/available-vehicles',
      { params: filters }
    );
    return response.data;
  }

  async allocateVehicleToOrder(
    orderNo: string,
    vehicleId: string,
    driverId: string | null = null,
    notes = ''
  ): Promise<ApiWriteResponse> {
    // The order number travels in the body here, not in the path.
    const response = await apiService.post<ApiWriteResponse>('/order-allocations', {
      order_no: orderNo,
      vehicle_id: vehicleId,
      driver_id: driverId,
      notes,
    });
    return response.data;
  }

  async updateAllocationStatus(
    allocationId: number | string,
    status: string
  ): Promise<ApiWriteResponse> {
    const response = await apiService.put<ApiWriteResponse>(
      `/order-allocations/${seg(allocationId)}/status`,
      { status }
    );
    return response.data;
  }

  async removeAllocation(allocationId: number | string): Promise<ApiWriteResponse> {
    const response = await apiService.delete<ApiWriteResponse>(
      `/order-allocations/${seg(allocationId)}`
    );
    return response.data;
  }

  // ===========================
  // UTILITY METHODS
  // ===========================

  /**
   * Format order number for display
   */
  formatOrderNumber(orderNo: string | null | undefined): string {
    return orderNo || 'N/A';
  }

  /**
   * Format order status with color
   */
  getStatusColor(status: string): string {
    const statusColors: Record<string, string> = {
      Active: 'blue',
      'In Progress': 'yellow',
      Completed: 'green',
      Cancelled: 'red',
      'On Hold': 'gray',
    };
    return statusColors[status] || 'gray';
  }

  /**
   * Format order priority with color
   */
  getPriorityColor(priority: string): string {
    const priorityColors: Record<string, string> = {
      Low: 'gray',
      Normal: 'blue',
      High: 'orange',
      Urgent: 'red',
    };
    return priorityColors[priority] || 'gray';
  }

  /**
   * Calculate completion percentage
   */
  calculateCompletionPercentage(
    orderSize: number | null | undefined,
    remaining: number | null | undefined
  ): number {
    if (!orderSize || orderSize === 0) return 0;
    const delivered = orderSize - (remaining || 0);
    return Math.round((delivered / orderSize) * 100);
  }

  /**
   * Format weight with units
   */
  formatWeight(weight: number | string | null | undefined, unit = 'Metric Tonnes'): string {
    if (weight === null || weight === undefined) return 'N/A';
    return `${parseFloat(String(weight)).toLocaleString()} ${unit}`;
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }

  /**
   * Validate order data before submission
   */
  validateOrderData(orderData: OrderDataCheckInput): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!orderData.order_no) {
      errors.push('Order number is required');
    }

    if (!orderData.order_size || Number(orderData.order_size) <= 0) {
      errors.push('Order size must be greater than 0');
    }

    const dispatchLoc = orderData.dispatch_location_name || orderData.dispatch_location_id;
    const receiptLoc = orderData.receipt_location_name || orderData.receipt_location_id;
    if (dispatchLoc && receiptLoc && dispatchLoc === receiptLoc) {
      errors.push('Dispatch and receipt locations must be different');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

// Export singleton instance
const orderService = new OrderService();
export default orderService;
export type { OrderService };
